import type { VehicleStrategy, VehiclePhysicsConfig } from './VehicleStrategy'

export class GoKartStrategy implements VehicleStrategy {
  private readonly config: VehiclePhysicsConfig = {
    productiveGForce: 1.2, // Go-karts can exceed 1.2g combined
    hardBrakingThreshold: -10.0, // Excellent braking (low CG, no weight transfer)
    hardAccelerationThreshold: 5.0, // Lightweight = good acceleration
    canLean: false,
    tractionLimitLeanAngle: undefined,
    minimumCorneringG: 1.0, // Need to corner very hard
    engineBrakeThreshold: 1.5, // Minimal engine braking
    moderateBrakingThreshold: -5.0,
    moderateAccelerationThreshold: 2.5,
  }

  public getVehicleType = (): string => {
    return 'go-kart'
  }

  public getPhysicsConfig = (): VehiclePhysicsConfig => {
    return this.config
  }

  public calculateCoastingIntensity = (
    acceleration: number,
    leanAngle: number, // Should always be ~0 for go-karts
    lateralG: number,
    combinedG: number,
    _speed: number
  ): number => {
    // Go-karts don't lean - ignore lean angle
    if (Math.abs(leanAngle) > 5) {
      // Suspicious data
      return 0.5
    }

    // Check combined g-force first - go-karts can achieve very high g
    if (combinedG >= this.config.productiveGForce) {
      return 0
    }

    // Very hard braking - go-karts brake exceptionally well
    if (acceleration < this.config.hardBrakingThreshold) {
      return 0
    }

    const isAccelerating = acceleration > 0
    const isBraking = acceleration < 0
    const absAcceleration = Math.abs(acceleration)

    // BRAKING LOGIC: Go-karts can brake very hard
    if (isBraking) {
      return this.calculateBrakingCoastingIntensity(absAcceleration, lateralG)
    }

    // ACCELERATION LOGIC: Lightweight = good acceleration
    if (isAccelerating) {
      const accelIntensity =
        this.calculateAccelerationCoastingIntensity(absAcceleration)
      if (accelIntensity !== null) {
        return accelIntensity
      }
      // Fall through to combined g-force logic
    }

    // Neutral or low acceleration - use combined g-force
    return this.calculateCombinedGForceCoastingIntensity(combinedG, lateralG)
  }

  private readonly calculateBrakingCoastingIntensity = (
    absAcceleration: number,
    lateralG: number
  ): number => {
    if (absAcceleration < this.config.engineBrakeThreshold) {
      // Very light braking - go-karts have minimal engine braking
      if (lateralG < 0.7) {
        // Not cornering hard - should be braking
        // 0 m/s² = 95% coasting, 1.5 m/s² = 60% coasting
        const intensity =
          0.95 - (absAcceleration / this.config.engineBrakeThreshold) * 0.35
        return Math.max(0.6, intensity)
      }
      // Cornering moderately - might be appropriate
      return 0.4
    } else if (absAcceleration < 5.0) {
      // Light to moderate braking (1.5-5 m/s²)
      if (lateralG < 0.8) {
        // Not cornering hard enough - should brake more
        // 1.5 m/s² = 60%, 5 m/s² = 20%
        const intensity = 0.6 - ((absAcceleration - 1.5) / 3.5) * 0.4
        return Math.max(0.2, intensity)
      }
      // Cornering hard
      return 0.15
    } else if (absAcceleration < 10.0) {
      // Moderate to hard braking (5-10 m/s²)
      if (lateralG < 0.5) {
        // Gentle cornering - could brake even harder
        // 5 m/s² = 20%, 10 m/s² = 2%
        const intensity = 0.2 - ((absAcceleration - 5.0) / 5.0) * 0.18
        return Math.max(0.02, intensity)
      }
      // Cornering while braking
      return 0
    }

    // Very hard braking (>10 m/s²) - exceptional performance
    return 0
  }

  private readonly calculateAccelerationCoastingIntensity = (
    absAcceleration: number
  ): number | null => {
    if (absAcceleration >= this.config.hardAccelerationThreshold) {
      // Good acceleration (>5 m/s²) - full throttle
      return 0
    } else if (absAcceleration >= this.config.moderateAccelerationThreshold) {
      // Moderate acceleration (2.5-5 m/s²) - could be full throttle
      // Scale: 5 m/s² = 0%, 2.5 m/s² = 15%
      const intensity =
        ((5.0 - absAcceleration) / this.config.moderateAccelerationThreshold) *
        0.15
      return Math.max(0, intensity)
    }

    // Low acceleration (<2.5 m/s²) - use combined g-force logic
    return null
  }

  private readonly calculateCombinedGForceCoastingIntensity = (
    combinedG: number,
    lateralG: number
  ): number => {
    // Check if cornering hard enough - go-karts should corner very hard
    if (lateralG >= this.config.minimumCorneringG) {
      return 0 // Cornering at limit
    }

    let intensity: number

    if (combinedG >= this.config.productiveGForce) {
      // >1.2g combined - exceptional performance
      intensity = 0
    } else if (combinedG >= 0.9) {
      // 0.9-1.2g combined - good performance
      // Linear scale: 1.2g = 0%, 0.9g = 30%
      intensity = ((this.config.productiveGForce - combinedG) / 0.3) * 0.3
    } else if (combinedG >= 0.6) {
      // 0.6-0.9g combined - moderate effort
      // Linear scale: 0.9g = 30%, 0.6g = 60%
      intensity = 0.3 + ((0.9 - combinedG) / 0.3) * 0.3
    } else {
      // <0.6g combined - coasting
      // Scale: 0.6g = 60%, 0g = 100%
      intensity = 0.6 + ((0.6 - combinedG) / 0.6) * 0.4
    }

    // Clamp to valid range [0, 1]
    return Math.max(0, Math.min(1, intensity))
  }

  public validateTelemetry = (
    _acceleration: number,
    leanAngle: number,
    _speed: number
  ): boolean => {
    // Go-karts should never lean
    return Math.abs(leanAngle) < 10
  }
}
